import { apiQueryAdapter } from "../apiQueryAdapter";
import {
  LocationMasterDto,
  LocationMasterListParams,
  LocationMasterRequest,
  LocationSummaryCountDto,
  LocationCloseRequest,
  LocationSwingRequest,
  LocationActivityLogModel,
} from "./models";
import { PageResponseModel } from "../common/models";

class LocationMasterService {
  private static instance: LocationMasterService;

  private constructor() {}

  static getInstance(): LocationMasterService {
    if (!LocationMasterService.instance) {
      LocationMasterService.instance = new LocationMasterService();
    }
    return LocationMasterService.instance;
  }

  /**
   * Get a paginated list of locations for a terminal with optional filtering
   * GET /location-master/list
   */
  public getLocationList = async (
    params: LocationMasterListParams
  ): Promise<PageResponseModel<LocationMasterDto>> => {
    const queryParams: Record<string, any> = {};

    // Optional parameters
    if (params.terminalNumber !== undefined && params.terminalNumber !== null) {
      queryParams.terminalNumber = params.terminalNumber.toString();
    }
    if (params.locationTypeFilter) {
      queryParams.locationTypeFilter = params.locationTypeFilter;
    }
    if (params.subTypeFilter) {
      queryParams.subTypeFilter = params.subTypeFilter;
    }
    if (params.lifecycleStateFilter) {
      queryParams.lifecycleStateFilter = params.lifecycleStateFilter;
    }
    if (params.search && params.search.trim() !== "") {
      queryParams.search = params.search.trim();
    }
    if (params.locationOccupancyFilter) {
      queryParams.locationOccupancyFilter = params.locationOccupancyFilter;
    }
    if (params.page !== undefined) {
      queryParams.page = params.page.toString();
    }
    if (params.size !== undefined) {
      queryParams.size = params.size.toString();
    }
    if (params.sort) {
      queryParams.sort = params.sort;
    }

    const response = await apiQueryAdapter.query<PageResponseModel<LocationMasterDto>>(
      "/location-master/list",
      "GET",
      { params: queryParams }
    );

    return response.data;
  };

  /**
   * Get a single location by its UUID
   * GET /location-master/{id}
   */
  public getLocationById = async (id: string): Promise<LocationMasterDto> => {
    const response = await apiQueryAdapter.query<LocationMasterDto>(
      `/location-master/${id}`,
      "GET"
    );

    return response.data;
  };

  /**
   * Get a location by terminal number and location code
   * GET /location-master/terminal/{terminalNumber}/location/{locationCode}
   */
  public getLocationByTerminalAndCode = async (
    terminalNumber: number | string,
    locationCode: string
  ): Promise<LocationMasterDto> => {
    const response = await apiQueryAdapter.query<LocationMasterDto>(
      `/location-master/terminal/${terminalNumber}/location/${locationCode}`,
      "GET"
    );

    return response.data;
  };

  /**
   * Get summary counts for Location Master overview tiles
   * GET /location-master/summary-counts
   */
  public getSummaryCounts = async (
    terminalNumber?: number | string
  ): Promise<LocationSummaryCountDto> => {
    const params: Record<string, any> = {};

    if (terminalNumber !== undefined) {
      params.terminalNumber = terminalNumber.toString();
    }

    const response = await apiQueryAdapter.query<LocationSummaryCountDto>(
      "/location-master/summary-counts",
      "GET",
      { params }
    );

    return response.data;
  };

  /**
   * Create a new location
   * POST /location-master
   */
  public createLocation = async (
    request: LocationMasterRequest
  ): Promise<LocationMasterDto> => {
    const params: Record<string, any> = {};
    if (request.terminalNumber !== undefined && request.terminalNumber !== null) {
      params.terminalNumber = request.terminalNumber.toString();
    }

    const response = await apiQueryAdapter.query<LocationMasterDto>(
      "/location-master",
      "POST",
      { params, body: request }
    );

    return response.data;
  };

  /**
   * Update an existing location
   * PUT /location-master/{id}
   */
  public updateLocation = async (
    id: string,
    request: LocationMasterRequest
  ): Promise<LocationMasterDto> => {
    const response = await apiQueryAdapter.query<LocationMasterDto>(
      `/location-master/${id}`,
      "PUT",
      { body: request }
    );

    return response.data;
  };

  /**
   * Delete a location by ID
   * DELETE /location-master/{id}
   */
  public deleteLocation = async (id: string): Promise<void> => {
    await apiQueryAdapter.query<void>(`/location-master/${id}`, "DELETE");
  };

  /**
   * Close a location (marks it as closed in the lifecycle)
   * POST /location-master/{id}/close
   */
  public closeLocation = async (
    id: string,
    request?: LocationCloseRequest
  ): Promise<LocationMasterDto> => {
    const response = await apiQueryAdapter.query<LocationMasterDto>(
      `/location-master/${id}/close`,
      "POST",
      { body: request || {} }
    );

    return response.data;
  };

  /**
   * Process an overweight manifest at a location
   * POST /location-master/{id}/overweight
   */
  public processOverweight = async (
    id: string,
    manifestNumber: string
  ): Promise<LocationMasterDto> => {
    const response = await apiQueryAdapter.query<LocationMasterDto>(
      `/location-master/${id}/overweight`,
      "POST",
      { params: { manifestNumber } }
    );

    return response.data;
  };

  /**
   * Process a return manifest at a location
   * POST /location-master/{id}/return
   */
  public processReturn = async (
    id: string,
    manifestNumber: string
  ): Promise<LocationMasterDto> => {
    const response = await apiQueryAdapter.query<LocationMasterDto>(
      `/location-master/${id}/return`,
      "POST",
      { params: { manifestNumber } }
    );

    return response.data;
  };

  /**
   * Swing a load from one location to another
   * POST /location-master/swing
   */
  public swingLoad = async (
    terminalNumber: number | string,
    request: LocationSwingRequest
  ): Promise<void> => {
    await apiQueryAdapter.query<void>(
      "/location-master/swing",
      "POST",
      { params: { terminalNumber: terminalNumber.toString() }, body: request }
    );
  };

  /**
   * Assign a trailer (and optionally a carrier) to a location
   * POST /location-master/{id}/assign-trailer
   */
  public assignTrailer = async (
    id: string,
    trailerNumber: string,
    carrierNumber?: string
  ): Promise<LocationMasterDto> => {
    const params: Record<string, any> = { trailerNumber };
    if (carrierNumber) {
      params.carrierNumber = carrierNumber;
    }

    const response = await apiQueryAdapter.query<LocationMasterDto>(
      `/location-master/${id}/assign-trailer`,
      "POST",
      { params }
    );

    return response.data;
  };

  /**
   * Remove trailer assignment from a location
   * POST /location-master/{id}/remove-trailer
   */
  public removeTrailer = async (id: string): Promise<LocationMasterDto> => {
    const response = await apiQueryAdapter.query<LocationMasterDto>(
      `/location-master/${id}/remove-trailer`,
      "POST"
    );

    return response.data;
  };

  /**
   * Start the loading process for a location
   * POST /location-master/{id}/start-loading
   */
  public startLoading = async (id: string): Promise<LocationMasterDto> => {
    const response = await apiQueryAdapter.query<LocationMasterDto>(
      `/location-master/${id}/start-loading`,
      "POST"
    );

    return response.data;
  };

  /**
   * Start the unloading process for a location
   * POST /location-master/{id}/start-unloading
   */
  public startUnloading = async (id: string): Promise<LocationMasterDto> => {
    const response = await apiQueryAdapter.query<LocationMasterDto>(
      `/location-master/${id}/start-unloading`,
      "POST"
    );

    return response.data;
  };

  /**
   * Stage freight at a location
   * POST /location-master/{id}/stage-freight
   */
  public stageFreight = async (
    id: string,
    notes?: string
  ): Promise<LocationMasterDto> => {
    const response = await apiQueryAdapter.query<LocationMasterDto>(
      `/location-master/${id}/stage-freight`,
      "POST",
      { body: notes ? { notes } : {} }
    );

    return response.data;
  };

  /**
   * Reopen a closed location
   * POST /location-master/{id}/reopen
   */
  public reopenLocation = async (
    id: string,
    notes?: string
  ): Promise<LocationMasterDto> => {
    const response = await apiQueryAdapter.query<LocationMasterDto>(
      `/location-master/${id}/reopen`,
      "POST",
      { body: notes ? { notes } : {} }
    );

    return response.data;
  };

  /**
   * Get location activity log list with filters and pagination
   * GET /location-master/activity-log/all
   */
  public getLocationActivityLogList = async (
    page?: number,
    search?: string,
    action?: string,
    dateFrom?: string,
    dateTo?: string,
    size: number = 50
  ): Promise<PageResponseModel<LocationActivityLogModel>> => {
    const params: Record<string, any> = {};

    if (page !== undefined) params.page = page.toString();
    if (size !== undefined) params.size = size.toString();
    if (search && search.trim() !== "") params.search = search.trim();
    if (action && action.trim() !== "") params.action = action.trim();
    if (dateFrom && dateFrom.trim() !== "") params.dateFrom = dateFrom.trim();
    if (dateTo && dateTo.trim() !== "") params.dateTo = dateTo.trim();

    const response = await apiQueryAdapter.query<PageResponseModel<LocationActivityLogModel>>(
      "/location-master/activity-log/all",
      "GET",
      { params }
    );

    return response.data;
  };
}

export default LocationMasterService;

